import React, { useState } from 'react'
import styled from 'styled-components'

import { Button } from '@/components/Button'
import Icons from '@/components/Icons'
import { InputText } from '@/components/InputText'
import { TextArea } from '@/components/Inputs'
import { Text } from '@/components/Text'

import { ColorsMap, device } from '@/utils/Theme'
import useTranslation from 'next-translate/useTranslation'

type Props = {
  contact: {
    name: string
    email?: string
    phone: string
    imageSrc?: string
  }
  secondaryContact?: {
    name: string
    email?: string
    phone: string
  }
  handleSubmit: (e: React.FormEvent, payload, actions) => Promise<boolean>
}

const ContactForm = ({ contact, secondaryContact, handleSubmit }: Props) => {
  const { t } = useTranslation('common')

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [content, setContent] = useState('')

  const [errors, setErrors] = useState<{ [k: string]: string[] }>({})

  const [buttonDisabled, setButtonDisabled] = useState(false)

  const validateRequired = (name, value, callback) => {
    if (!value) {
      callback(name, 'Pole je povinné')
    }
  }

  const validateEmail = (name, value, callback) => {
    if (
      !/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
        value,
      )
    ) {
      callback(name, 'Pole musí byť email')
    }
  }

  const validateForm = () => {
    const localErrors = {}

    const callback = (name: string, error: string) => {
      localErrors[name] = [...(localErrors[name] || []), error]
    }

    validateRequired('name', name, callback)
    validateRequired('email', email, callback)
    validateEmail('email', email, callback)
    validateRequired('phone', phone, callback)
    validateRequired('content', content, callback)

    setErrors(localErrors)

    return Object.keys(localErrors)
  }

  return (
    <FormWrapper>
      <form
        action="#"
        method="POST"
        onSubmit={(e) =>
          handleSubmit(e, { name, email, phone, content, sendTo: contact.email }, {
            validate: validateForm,
            setLoading: setButtonDisabled,
          })
        }
      >
        <InputGroup>
          <InputText
            label={
              <Text as="span" textColor="primary" fontWeight="bold">
                {t('name')}
              </Text>
            }
            name="name"
            id="name"
            value={name}
            invalid={!!errors?.name}
            onChange={(e) => setName(e.target.value)}
          />
        </InputGroup>
        <InputGroup>
          <InputText
            label={
              <Text as="span" textColor="primary" fontWeight="bold">
                {t('email')}
              </Text>
            }
            name="email"
            id="email"
            value={email}
            invalid={!!errors.email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <InputText
            label={
              <Text as="span" textColor="primary" fontWeight="bold">
                {t('mobile-number')}
              </Text>
            }
            name="phone"
            id="phone"
            value={phone}
            invalid={!!errors.phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </InputGroup>
        <TextArea
          name="content"
          id="content"
          onChange={(e) => setContent(e.target.value)}
          value={content}
          invalid={!!errors.content}
          my="1rem"
        >
          <Text textColor="primary" fontWeight="bold" mt="1rem">
            {t('content')}
          </Text>
        </TextArea>
        <Button width="100%" variant="horizontalRad" colorVariant="primary" type="submit" disabled={buttonDisabled}>
          {t('send')}
        </Button>
      </form>

      <ContactWrapper>
        <Text textColor="white" fontWeight="bold" fontSize="xl" mb="1rem">
          {t('contact-info')}
        </Text>
        <Separator />
        {contact.imageSrc &&
          <ContactImg src={contact.imageSrc} alt={contact.name} />
        }
        <Text textColor="white" fontWeight="bold" mb="1rem">
          {contact.name}
        </Text>
        {contact.email && (
          <Text textColor="white" mb="1rem">
            <Icons.Envelope color="white" mr="0.5rem" />
            {contact.email}
          </Text>
        )}
        <Text textColor="white">
          <Icons.Phone color="white" mr="0.5rem" />
          {contact.phone}
        </Text>
        {secondaryContact && (
          <>
            <Text textColor="white" fontWeight="bold" my="1rem">
              {secondaryContact.name}
            </Text>
            {secondaryContact.email && (
              <Text textColor="white" mb="1rem">
                <Icons.Envelope color="white" mr="0.5rem" />
                {secondaryContact.email}
              </Text>
            )}
            <Text textColor="white">
              <Icons.Phone color="white" mr="0.5rem" />
              {secondaryContact.phone}
            </Text>
          </>
        )}

      </ContactWrapper>
    </FormWrapper>
  )
}

export default ContactForm

const FormWrapper = styled.div`
    display: grid;
    grid-template-columns: 5fr 2fr;
    gap: 4rem;
    padding: 3rem;
    background-color: ${ColorsMap.white};
    margin-bottom: 4rem;

    @media (max-width: ${device.tablet}) {
        grid-template-columns: 1fr;
    }
`

const ContactWrapper = styled.div`
    padding: 3rem;
    background-color: ${ColorsMap.primary};

    @media (max-width: ${device.tablet}) {
        padding: 2rem;
    }
`

const ContactImg = styled.img`
    max-width: 6rem;
    width: 100%;
    margin-bottom: 1rem;
`

const InputGroup = styled.div`
    display: flex;
    column-gap: 2rem;

    > * {
        width: 100%;
    }

    @media (max-width: ${device.tablet}) {
        flex-direction: column;
    }
`

const Separator = styled.hr`
    margin: 1rem 0;
    border: 1px solid ${ColorsMap.lightGrey};
`
